import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitBranch, KeyRound, Loader2, Pause, Play, Plus, Rocket, Terminal, Timer, Trash2, Webhook } from "lucide-react";
import { useState } from "react";
import { ApiError, type SiteTools } from "@hostdock/sdk";
import type { CronJobRecord, CronRunRecord, WebsiteDetail } from "@hostdock/types";
import { StatusBadge } from "../badge";
import { TechLogo } from "../tech-logo";
import { Button } from "../button";
import { ConfirmDialog, Dialog } from "../dialog";
import { EmptyState, ErrorState, FormError, Notice } from "../feedback";
import { Field, Input, Select, Switch, Textarea } from "../form";
import { formatDateTime, formatRelative } from "../format";
import { ActionMenu } from "../menu";
import { CopyButton, OneTimeSecret } from "../secret";
import { toast } from "../toast";
import { InfoTile, TabCard, tabPrimaryClass, tabSecondaryClass } from "./tab-card";

const deployedStatus = (w: WebsiteDetail) => w.status === "ACTIVE" || w.status === "SUSPENDED" || w.status === "FAILED";

// ---------------------------------------------------------------- SFTP

export function WebsiteSftp({ w, tools, canManage }: { w: WebsiteDetail; tools: SiteTools; canManage: boolean }) {
  const qc = useQueryClient();
  const key = ["sftp", w.id];
  const q = useQuery({ queryKey: key, queryFn: () => tools.sftp.get(w.id), enabled: deployedStatus(w) });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useMutation({ meta: { inlineError: true }, mutationFn: () => tools.sftp.resetPassword(w.id), onSuccess: (r) => { setPassword(r.password); setConfirmReset(false); refresh(); } });
  const disable = useMutation({ meta: { inlineError: true }, mutationFn: () => tools.sftp.disablePassword(w.id), onSuccess: () => { toast.success("Password sign-in disabled"); setPassword(null); refresh(); } });
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => tools.sftp.removeKey(w.id, id), onSuccess: () => { toast.success("Key removed"); setRemoving(null); refresh(); } });

  if (!deployedStatus(w)) return <EmptyState title="SFTP is available once the website is deployed" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const s = q.data;
  const manage = canManage && s?.available !== false;
  return (
    <div className="flex flex-col gap-4">
      {s && !s.available && <Notice tone="warning">This website is suspended, so SFTP access is disabled.</Notice>}
      <TabCard icon={Terminal} title="Connection" description="SFTP only (no shell). You are confined to this website's files: public/, app/, logs/ and tmp/."
        action={manage && (
          <>
            {s?.passwordEnabled && <Button variant="ghost" loading={disable.isPending} onClick={() => disable.mutate()}>Disable password sign-in</Button>}
            <button type="button" className={tabPrimaryClass} onClick={() => setConfirmReset(true)}><KeyRound size={15} />{s?.passwordEnabled ? "Generate new password" : "Enable password sign-in"}</button>
          </>
        )}>
        {s ? (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <InfoTile label="Host" value={s.host ?? "—"} copy={s.host} />
            <InfoTile label="Port" value={s.port} copy={String(s.port)} />
            <InfoTile label="Username" value={s.username} copy={s.username} />
            <InfoTile label="Password sign-in" mono={false} value={s.passwordEnabled ? <span className="text-success">Enabled · set {formatRelative(s.passwordSetAt)}</span> : <span className="text-muted">Disabled</span>} />
          </div>
        ) : <p className="text-[13px] text-muted">Loading…</p>}
        {s?.host && <p className="mt-3 rounded-lg bg-surface-2/60 px-[12px] py-[8px] font-mono text-[11.5px] text-muted">sftp -P {s.port} {s.username}@{s.host}</p>}
        {disable.error ? <div className="mt-3"><FormError error={disable.error} /></div> : null}
        {password && s && <div className="mt-4"><OneTimeSecret label={`Password for ${s.username}`} value={password} note="Copy it now. HostDock does not store it and cannot show it again." /></div>}
      </TabCard>
      <TabCard icon={KeyRound} title="SSH keys" count={s?.keys.length} flush description="Key sign-in is recommended. Keys apply to SFTP for this website only."
        action={manage && <button type="button" className={tabSecondaryClass} onClick={() => setAdding(true)}><Plus size={15} />Add key</button>}>
        {q.isPending ? <p className="px-[16px] py-5 text-[13px] text-muted">Loading…</p> : !s?.keys.length ? (
          <EmptyState icon={KeyRound} title="No SSH keys" description="Add a public key to sign in without a password." />
        ) : (
          <ul className="divide-y divide-line">
            {s.keys.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-[16px] py-[12px]">
                <span className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-lg bg-surface-3 text-muted" aria-hidden><KeyRound size={15} /></span>
                <div className="min-w-0 flex-[1_1_14rem] leading-tight">
                  <p className="truncate text-[13.5px] font-semibold">{k.name}</p>
                  <p className="mt-[3px] truncate font-mono text-[11.5px] text-muted" title={`${k.type} ${k.fingerprint}`}>{k.type} {k.fingerprint}</p>
                </div>
                <span className="text-[12px] text-subtle">Added {formatRelative(k.createdAt)}</span>
                {canManage && <Button size="sm" variant="danger-ghost" aria-label={`Remove ${k.name}`} icon={<Trash2 size={13} />} onClick={() => setRemoving(k)} />}
              </li>
            ))}
          </ul>
        )}
      </TabCard>
      {adding && <AddKeyDialog onClose={() => setAdding(false)} onAdd={(name, publicKey) => tools.sftp.addKey(w.id, name, publicKey)} onDone={refresh} />}
      <ConfirmDialog open={confirmReset} onOpenChange={setConfirmReset} tone="primary" title={s?.passwordEnabled ? "Generate a new SFTP password?" : "Enable password sign-in?"}
        description={s?.passwordEnabled ? "The current password stops working immediately." : "A strong password is generated and shown once."}
        confirmLabel="Generate password" loading={reset.isPending} error={reset.error} onConfirm={() => reset.mutate()} />
      <ConfirmDialog open={removing !== null} onOpenChange={(o) => !o && setRemoving(null)} title={`Remove key ${removing?.name}?`} description="Anyone using this key can no longer sign in." confirmLabel="Remove key" loading={remove.isPending} error={remove.error} onConfirm={() => removing && remove.mutate(removing.id)} />
    </div>
  );
}

function AddKeyDialog({ onClose, onAdd, onDone }: { onClose(): void; onAdd(name: string, key: string): Promise<unknown>; onDone(): void }) {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const m = useMutation({ meta: { inlineError: true }, mutationFn: () => onAdd(name.trim(), key.trim()), onSuccess: () => { toast.success("Key added"); onDone(); onClose(); } });
  const fe = m.error instanceof ApiError ? m.error.fieldErrors : {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Add SSH key"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!name.trim() || !key.trim()} onClick={() => m.mutate()}>Add key</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Name" error={fe.name}>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="Work laptop" autoFocus />}</Field>
        <Field label="Public key" hint="The contents of ~/.ssh/id_ed25519.pub (never the private key)." error={fe.publicKey}>
          {(p) => <Textarea {...p} value={key} onChange={(e) => setKey(e.target.value)} rows={4} className="font-mono text-[12px]" placeholder="ssh-ed25519 AAAA… you@laptop" spellCheck={false} />}
        </Field>
        {m.error && !Object.keys(fe).length ? <FormError error={m.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Cron

const CRON_PRESETS = [
  { label: "Every 5 minutes", value: "*/5 * * * *" },
  { label: "Every 15 minutes", value: "*/15 * * * *" },
  { label: "Hourly", value: "0 * * * *" },
  { label: "Daily at 03:00", value: "0 3 * * *" },
  { label: "Weekly (Sunday 03:00)", value: "0 3 * * 0" },
];

export function WebsiteCron({ w, tools, canManage }: { w: WebsiteDetail; tools: SiteTools; canManage: boolean }) {
  const qc = useQueryClient();
  const key = ["cron", w.id];
  const q = useQuery({ queryKey: key, queryFn: () => tools.cron.list(w.id), refetchInterval: 15_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const [editing, setEditing] = useState<CronJobRecord | "new" | null>(null);
  const [deleting, setDeleting] = useState<CronJobRecord | null>(null);
  const [history, setHistory] = useState<CronJobRecord | null>(null);
  const run = useMutation({ mutationFn: (j: CronJobRecord) => tools.cron.run(w.id, j.id), onSuccess: (r, j) => { toast.success(r.status === "SKIPPED" ? "Skipped: the previous run is still in progress" : "Job started"); setHistory(j); refresh(); }, onError: (e) => toast.error((e as Error).message) });
  const toggle = useMutation({ mutationFn: (j: CronJobRecord) => tools.cron.update(w.id, j.id, { enabled: !j.enabled }), onSuccess: refresh, onError: (e) => toast.error((e as Error).message) });
  const remove = useMutation({ mutationFn: (j: CronJobRecord) => tools.cron.remove(w.id, j.id), onSuccess: () => { toast.success("Cron job deleted"); setDeleting(null); refresh(); } });
  const manage = canManage && w.status !== "SUSPENDED" && deployedStatus(w);
  const d = q.data;
  const atLimit = d ? d.items.length >= d.limit : false;
  return (
    <div className="flex flex-col gap-3">
      {w.status === "SUSPENDED" && <Notice tone="warning">This website is suspended. Scheduled jobs do not run.</Notice>}
      <TabCard icon={Timer} title="Cron jobs" count={d?.items.length} flush
        description={d ? `Commands run inside the website's container as the site user. Times are in ${d.timezone}. Your plan allows ${d.limit} job${d.limit === 1 ? "" : "s"}.` : undefined}
        action={manage && <button type="button" className={tabPrimaryClass} disabled={atLimit} onClick={() => setEditing("new")}><Plus size={15} />Add job</button>}>
        {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} className="m-3" />
          : q.isPending ? <p className="px-[16px] py-5 text-[13px] text-muted">Loading…</p>
          : !d?.items.length ? <EmptyState icon={Timer} title="No cron jobs" description="Schedule recurring tasks such as WordPress cron or cache cleanup." />
          : (
            <ul className="divide-y divide-line">
              {d.items.map((j) => (
                <li key={j.id} className="flex flex-wrap items-center gap-x-5 gap-y-2.5 px-[16px] py-[12px]">
                  <div className="flex min-w-0 flex-[1_1_16rem] items-center gap-[12px]">
                    <span className={j.enabled ? "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent" : "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-surface-3 text-muted"} aria-hidden>
                      {j.enabled ? <Timer size={16} /> : <Pause size={16} />}
                    </span>
                    <div className="min-w-0 leading-tight">
                      <p className="truncate text-[13.5px] font-semibold">{j.name}</p>
                      <p className="mt-[3px] truncate font-mono text-[11.5px] text-muted" title={j.command}>{j.command}</p>
                    </div>
                  </div>
                  <span className="rounded-md bg-surface-3 px-[8px] py-[3px] font-mono text-[12px] whitespace-nowrap text-fg/85" title="Schedule">{j.schedule}</span>
                  <div className="min-w-[9rem] leading-tight">
                    <p className="text-[10.5px] tracking-wide text-subtle uppercase">Next run</p>
                    <p className="mt-[3px] text-[12.5px] whitespace-nowrap">{j.enabled ? (j.nextRunAt ? formatDateTime(j.nextRunAt) : "—") : <span className="text-muted">Paused</span>}</p>
                  </div>
                  <div className="min-w-[7rem] leading-tight">
                    <p className="mb-[3px] text-[10.5px] tracking-wide text-subtle uppercase">Last result</p>
                    {j.lastStatus ? <StatusBadge status={j.lastStatus} /> : <span className="text-[12.5px] text-muted">Never run</span>}
                  </div>
                  <ActionMenu
                    label={`Actions for ${j.name}`}
                    actions={[
                      { label: "Run now", icon: <Play size={14} />, hidden: !manage || w.status !== "ACTIVE", onSelect: () => run.mutate(j) },
                      { label: "Run history", onSelect: () => setHistory(j) },
                      { label: "Edit", hidden: !manage, onSelect: () => setEditing(j) },
                      { label: j.enabled ? "Pause" : "Resume", hidden: !manage, onSelect: () => toggle.mutate(j) },
                      { label: "Delete", icon: <Trash2 size={14} />, danger: true, hidden: !canManage, onSelect: () => setDeleting(j) },
                    ]}
                  />
                </li>
              ))}
            </ul>
          )}
        {d && d.items.length > 0 && <p className="border-t border-line px-[16px] py-[9px] text-[12px] text-muted">{d.items.length} of {d.limit} jobs used{atLimit ? " · delete one to add another" : ""}</p>}
      </TabCard>
      {editing && (
        <CronDialog job={editing === "new" ? null : editing} timezone={d?.timezone ?? "UTC"} onClose={() => setEditing(null)} onDone={refresh}
          onSave={(input) => (editing === "new" ? tools.cron.create(w.id, input) : tools.cron.update(w.id, editing.id, input))} />
      )}
      {history && <CronHistoryDialog job={history} load={() => tools.cron.runs(w.id, history.id)} loadRun={(runId) => tools.cron.runDetail(w.id, history.id, runId)} onClose={() => { setHistory(null); refresh(); }} />}
      <ConfirmDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)} title={`Delete ${deleting?.name}?`} description="The job and its run history are deleted." confirmLabel="Delete job" loading={remove.isPending} error={remove.error} onConfirm={() => deleting && remove.mutate(deleting)} />
    </div>
  );
}

function CronDialog({ job, timezone, onClose, onSave, onDone }: {
  job: CronJobRecord | null; timezone: string; onClose(): void; onDone(): void;
  onSave(input: { name: string; schedule: string; command: string; timeoutSeconds: number; enabled: boolean }): Promise<unknown>;
}) {
  const [f, setF] = useState({ name: job?.name ?? "", schedule: job?.schedule ?? "*/15 * * * *", command: job?.command ?? "", timeoutSeconds: job?.timeoutSeconds ?? 300, enabled: job?.enabled ?? true });
  const m = useMutation({ mutationFn: () => onSave(f), onSuccess: () => { toast.success(job ? "Cron job updated" : "Cron job added"); onDone(); onClose(); } });
  const fe = m.error instanceof ApiError ? m.error.fieldErrors : {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={job ? `Edit ${job.name}` : "Add cron job"}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!f.name.trim() || !f.command.trim()} onClick={() => m.mutate()}>{job ? "Save" : "Add job"}</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Name" error={fe.name}>{(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="WordPress cron" autoFocus />}</Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Schedule" hint={`minute hour day month weekday (${timezone})`} error={fe.schedule}>
            {(p) => <Input {...p} value={f.schedule} onChange={(e) => setF({ ...f, schedule: e.target.value })} className="font-mono" />}
          </Field>
          <Field label="Preset">
            {(p) => (
              <Select {...p} value={CRON_PRESETS.find((x) => x.value === f.schedule)?.value ?? ""} onChange={(e) => e.target.value && setF({ ...f, schedule: e.target.value })}>
                <option value="">Custom</option>
                {CRON_PRESETS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </Select>
            )}
          </Field>
        </div>
        <Field label="Command" hint="Runs with sh in the website's home folder, e.g. php public/wp-cron.php" error={fe.command}>
          {(p) => <Input {...p} value={f.command} onChange={(e) => setF({ ...f, command: e.target.value })} className="font-mono" spellCheck={false} />}
        </Field>
        <Field label="Timeout (seconds)" error={fe.timeoutSeconds}>
          {(p) => <Input {...p} type="number" min={10} max={3600} value={f.timeoutSeconds} onChange={(e) => setF({ ...f, timeoutSeconds: Number(e.target.value) })} className="w-32" />}
        </Field>
        <Switch checked={f.enabled} onChange={(v) => setF({ ...f, enabled: v })} label="Enabled" />
        {m.error && !Object.keys(fe).length ? <FormError error={m.error} /> : null}
      </div>
    </Dialog>
  );
}

function CronHistoryDialog({ job, load, loadRun, onClose }: { job: CronJobRecord; load(): Promise<{ items: CronRunRecord[] }>; loadRun(id: string): Promise<CronRunRecord>; onClose(): void }) {
  const q = useQuery({ queryKey: ["cron-runs", job.id], queryFn: load, refetchInterval: (query) => (query.state.data?.items.some((r) => r.status === "RUNNING") ? 3000 : false) });
  const [open, setOpen] = useState<string | null>(null);
  const run = useQuery({ queryKey: ["cron-run", open], queryFn: () => loadRun(open!), enabled: Boolean(open) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Runs of ${job.name}`} description="The last 50 runs are kept." size="lg" footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      {q.error ? <FormError error={q.error} /> : !q.data ? <p className="text-[13px] text-muted">Loading…</p> : !q.data.items.length ? <p className="text-[13px] text-muted">This job has not run yet.</p> : (
        <ol className="max-h-[60vh] overflow-auto rounded-md border border-line">
          {q.data.items.map((r) => (
            <li key={r.id} className="border-b border-line last:border-0">
              <button type="button" className="flex w-full items-center gap-3 px-3 py-2 text-left text-[13px] hover:bg-surface-2/60" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
                <span className="flex-1">{formatDateTime(r.startedAt)} <span className="text-muted">· {r.trigger === "MANUAL" ? "manual" : "scheduled"}</span></span>
                {r.exitCode !== null && <span className="font-mono text-[12px] text-muted">exit {r.exitCode}</span>}
                <StatusBadge status={r.status} />
              </button>
              {open === r.id && (
                <pre className="max-h-72 overflow-auto border-t border-line bg-surface-2/40 p-3 font-mono text-[11.5px] whitespace-pre-wrap">
                  {r.status === "RUNNING" ? "Running…" : run.data?.id === r.id ? run.data.output || "No output." : "Loading…"}
                </pre>
              )}
            </li>
          ))}
        </ol>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- Git

export function WebsiteGit({ w, tools, canManage, onDeployed }: { w: WebsiteDetail; tools: SiteTools; canManage: boolean; onDeployed?(jobId: string): void }) {
  const qc = useQueryClient();
  const key = ["git", w.id];
  // After "Deploy now" the worker may not have marked the deployment RUNNING yet, so keep
  // polling until the server reports a deployment newer than the one seen at click time.
  const [awaiting, setAwaiting] = useState<{ since: string | null; until: number } | null>(null);
  const q = useQuery({
    queryKey: key,
    queryFn: () => tools.git.get(w.id),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.connected && d.lastStatus === "RUNNING") return 4000;
      if (awaiting && Date.now() < awaiting.until && (d?.connected ? d.lastSyncedAt : null) === awaiting.since) return 2000;
      return false;
    },
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const [form, setForm] = useState({ url: "", branch: "main", target: (w.runtime === "php" ? "public" : "app") as "app" | "public" });
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [webhook, setWebhook] = useState<{ url: string; secret: string } | null>(null);
  const connect = useMutation({ meta: { inlineError: true }, mutationFn: () => tools.git.connect(w.id, form), onSuccess: () => { toast.success("Repository connected. Add the deploy key to it, then deploy."); refresh(); } });
  const deploy = useMutation({ meta: { inlineError: true },
    mutationFn: () => tools.git.deploy(w.id),
    onSuccess: (r) => {
      toast.success("Deployment started");
      setAwaiting({ since: q.data?.connected ? q.data.lastSyncedAt : null, until: Date.now() + 120_000 });
      refresh();
      onDeployed?.(r.job.id);
    },
  });
  const disconnect = useMutation({ meta: { inlineError: true }, mutationFn: () => tools.git.disconnect(w.id), onSuccess: () => { toast.success("Repository disconnected"); setConfirmDisconnect(false); setWebhook(null); refresh(); } });
  const enableHook = useMutation({ mutationFn: () => tools.git.enableWebhook(w.id), onSuccess: (r) => { setWebhook({ url: new URL(r.url, window.location.origin).toString(), secret: r.secret }); refresh(); } });
  const disableHook = useMutation({ mutationFn: () => tools.git.disableWebhook(w.id), onSuccess: () => { toast.success("Webhook disabled"); setWebhook(null); refresh(); } });
  const fe = connect.error instanceof ApiError ? connect.error.fieldErrors : {};
  const manage = canManage && w.status !== "SUSPENDED";

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const g = q.data;
  if (!g) return <p className="text-[13px] text-muted">Loading…</p>;
  if (!g.connected) {
    if (!manage) return <EmptyState icon={GitBranch} title="No repository connected" />;
    return (
      <TabCard logo={<TechLogo tech="git" size="sm" />} title="Deploy from Git" description="HostDock fetches the branch with a read-only deploy key and checks it out into your website. Untracked files (uploads, caches) are kept.">
        <div className="flex max-w-xl flex-col gap-4">
          <Field label="Repository URL" hint="SSH (git@github.com:you/site.git) for private repositories, or HTTPS for public ones." error={fe.url}>
            {(p) => <Input {...p} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value.trim() })} className="font-mono" placeholder="git@github.com:you/site.git" spellCheck={false} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Branch" error={fe.branch}>{(p) => <Input {...p} value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value.trim() })} className="font-mono" />}</Field>
            <Field label="Deploy into">
              {(p) => (
                <Select {...p} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value as "app" })} disabled={w.runtime !== "php"}>
                  {w.runtime === "php" && <option value="public">public/ (web root)</option>}
                  <option value="app">app/</option>
                </Select>
              )}
            </Field>
          </div>
          {connect.error && !Object.keys(fe).length ? <FormError error={connect.error} /> : null}
          <div><button type="button" className={tabPrimaryClass} disabled={connect.isPending || !form.url} onClick={() => connect.mutate()}>{connect.isPending ? <Loader2 size={15} className="animate-spin" /> : <GitBranch size={15} />}Connect repository</button></div>
        </div>
      </TabCard>
    );
  }
  const running = g.lastStatus === "RUNNING";
  return (
    <div className="flex flex-col gap-4">
      <TabCard logo={<TechLogo tech="git" size="sm" />} title="Repository" description={<span className="font-mono break-all">{g.url}</span>}
        action={manage && (
          <>
            <Button variant="danger-ghost" onClick={() => setConfirmDisconnect(true)}>Disconnect</Button>
            <button type="button" className={tabPrimaryClass} disabled={deploy.isPending || w.status !== "ACTIVE" || running} onClick={() => deploy.mutate()}>
              {deploy.isPending || running ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />}{running ? "Deploying…" : "Deploy now"}
            </button>
          </>
        )}>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <InfoTile label="Branch" value={g.branch} />
          <InfoTile label="Deploys into" value={`${g.target}/`} />
          <InfoTile label="Last deployment" mono={false} value={g.lastStatus ? <span className="flex items-center gap-2"><StatusBadge status={g.lastStatus} />{g.lastSyncedAt && <span className="text-muted">{formatRelative(g.lastSyncedAt)}</span>}</span> : <span className="text-muted">Never deployed</span>} />
          <InfoTile label="Commit" value={g.lastCommit ? g.lastCommit.slice(0, 12) : "—"} copy={g.lastCommit} />
        </div>
        {g.lastStatus === "FAILED" && g.lastError && <div className="mt-3"><Notice tone="danger" title="Last deployment failed">{g.lastError}</Notice></div>}
        {deploy.error ? <div className="mt-3"><FormError error={deploy.error} /></div> : null}
      </TabCard>
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <TabCard icon={KeyRound} title="Deploy key" description="Add this public key to the repository as a read-only deploy key (GitHub: Settings → Deploy keys). Not needed for public HTTPS repositories."
          action={<CopyButton value={g.deployKey} label="Copy key" />}>
          <code className="block rounded-lg border border-line bg-surface-2/50 p-[10px] font-mono text-[11.5px] leading-[17px] break-all">{g.deployKey}</code>
        </TabCard>
        <TabCard icon={Webhook} title="Push to deploy" description="Deploy automatically when the branch is pushed. Works with GitHub (secret), GitLab (secret token) or any sender using an Authorization: Bearer header."
          action={manage && (
            <>
              {g.webhookEnabled && <Button variant="ghost" loading={disableHook.isPending} onClick={() => disableHook.mutate()}>Disable</Button>}
              <button type="button" className={tabSecondaryClass} disabled={enableHook.isPending} onClick={() => enableHook.mutate()}>{enableHook.isPending && <Loader2 size={14} className="animate-spin" />}{g.webhookEnabled ? "Regenerate secret" : "Enable webhook"}</button>
            </>
          )}>
          {webhook ? (
            <div className="flex flex-col gap-3">
              <div className="grid gap-2">
                <InfoTile label="Payload URL" value={webhook.url} copy={webhook.url} />
                <InfoTile label="Content type" value="application/json" />
              </div>
              <OneTimeSecret label="Webhook secret" value={webhook.secret} note="Paste it into the webhook's secret field. It is shown only once." />
            </div>
          ) : (
            <p className="flex items-center gap-2 text-[13px]">
              <span className={g.webhookEnabled ? "h-[8px] w-[8px] rounded-full bg-success" : "h-[8px] w-[8px] rounded-full bg-line-strong"} aria-hidden />
              {g.webhookEnabled ? "Enabled. Pushes to the branch deploy automatically." : "Disabled. Deploy with Deploy now, or enable the webhook."}
            </p>
          )}
        </TabCard>
      </div>
      <ConfirmDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect} title="Disconnect this repository?" description="The deploy key and webhook are deleted. Files already deployed stay in place." confirmLabel="Disconnect" loading={disconnect.isPending} error={disconnect.error} onConfirm={() => disconnect.mutate()} />
    </div>
  );
}
